import useResizeObserver from '@react-hook/resize-observer'
import { DateTime, Duration, Interval } from 'luxon'
import { useLayoutEffect, useMemo, useRef } from 'react'
import { DateFormatter } from '../components/date-formatter'

const FORMAT = {
	hour: 'H:mm',
	day: 'd.M.',
	week: 'd.M.',
} as const

function getTickType(seconds: number) {
	// duration below three days -> ticks in hours
	if (seconds <= 86400 * 3) {
		return 'hour'
	}
	// duration below 90 days -> ticks in days
	if (seconds <= 86400 * 30 * 3) {
		return 'day'
	}
	// duration above 90 days -> ticks in weeks
	return 'week'
}

export function Ticks({
	interval,
	seconds,
	containerRef,
}: {
	interval: Interval<true>
	seconds: number
	containerRef: HTMLDivElement | null
}) {
	const gridRef = useRef<HTMLDivElement>(null)

	useLayoutEffect(() => {
		if (!containerRef || !gridRef.current) return
		gridRef.current.style.height = containerRef.getBoundingClientRect().height - 1 + 'px'
	}, [containerRef])

	// Where the magic happens
	useResizeObserver(containerRef, entry => {
		if (!gridRef.current) return
		gridRef.current.style.height = entry.contentRect.height - 1 + 'px'
	})

	const ticks = useMemo(() => {
		const tickType = getTickType(seconds)
		const array = []
		const now = DateTime.now()
		const width =
			(Duration.fromObject({ [tickType]: 1 }).as('seconds') / interval.length('seconds')) *
			100
		let cursor = interval.start
		const stop = interval.end.startOf(tickType)
		while (cursor <= stop) {
			const left = (cursor.diff(interval.start, 'seconds').seconds / seconds) * 100
			let background = ''
			if (tickType === 'day' && cursor.weekday > 5) {
				background = 'bg-orange-50 opacity-80'
			}
			array.push(
				<div
					key={left}
					className={`absolute top-0 h-full border-l border-gray-200 ${background}`}
					style={{ left: left + '%', width: width + '%' }}
				>
					<span className="p-[1px] absolute top-0 right-full -rotate-90 w-max origin-top-right">
						<DateFormatter date={cursor} format={FORMAT[tickType]} />
					</span>
				</div>,
			)
			cursor = cursor.plus({ [tickType]: 1 })
		}
		if (interval.contains(now)) {
			const left = (now.diff(interval.start, 'seconds').seconds / seconds) * 100
			array.push(
				<div
					key="now"
					className={`absolute top-0 h-full border-l border-gray-500`}
					style={{ left: left + '%' }}
				>
					<span className="text-gray-500 bg-gray-100 p-[1px] absolute top-[1px] right-full -rotate-90 w-max origin-top-right">
						Nyní
					</span>
				</div>,
			)
		}
		array.push(<div key="end" className="w-[1px] bg-gray-200"></div>)
		return array
	}, [interval.start, interval.end, seconds])

	return (
		<div className="w-full h-full absolute leading-none" ref={gridRef}>
			{ticks}
		</div>
	)
}
